import type { DriverRow } from "@/types/driver";

/**
 * Defensive dedup of roster rows by `workspaceMembershipId`.
 *
 * The roster query joins through alias feeds and `feed_group_membership`, so a
 * single workspace membership can produce more than one row when the local
 * pglite database holds a stale alias feed or group-membership row (for
 * example, immediately after a driver is moved between groups). The view
 * should always be unique per driver — keep the newest entry and drop older
 * duplicates.
 *
 * Feed IDs are cuids (roughly time-sortable), so the lexically larger
 * `feedId` is treated as the newer entry, falling back to
 * `membershipAvailabilityUpdatedAt` when both rows lack a feed.
 */
export function dedupeDriversByMembership(rows: DriverRow[]): DriverRow[] {
  const byMembership = new Map<string, DriverRow>();
  for (const row of rows) {
    const existing = byMembership.get(row.workspaceMembershipId);
    if (!existing || isNewerEntry(row, existing)) {
      byMembership.set(row.workspaceMembershipId, row);
    }
  }
  return [...byMembership.values()];
}

function isNewerEntry(candidate: DriverRow, existing: DriverRow): boolean {
  const candidateFeed = candidate.feedId ?? "";
  const existingFeed = existing.feedId ?? "";
  if (candidateFeed !== existingFeed) return candidateFeed > existingFeed;

  const candidateUpdated = candidate.membershipAvailabilityUpdatedAt ?? "";
  const existingUpdated = existing.membershipAvailabilityUpdatedAt ?? "";
  return candidateUpdated > existingUpdated;
}
